import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Row } from 'react-styled-flexboxgrid';
import { navigate } from 'gatsby';
import { GatsbyImage, withArtDirection } from 'gatsby-plugin-image';
import { getGatsbyImage } from 'gatsby-plugin-storyblok-image';
import { storyblokEditable } from 'gatsby-source-storyblok';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { PRODUCT_URL_GROGU } from '../../constants';
import { useAppState } from '../../context/app-state.context';
import { useIsomorphicLayoutEffect } from '../../hooks';
import PauseSvgIcon from '../../icons/pause-button.svg';
import PlaySvgIcon from '../../icons/play-button.svg';
import { inputLabels } from '../../state/settings/settingsReducer';
import { btnLgPrimaryThinOrangeStyles, btnSmPrimaryThinOrangeStyles } from '../../styles/buttons';
import {
  bodyLRegularUtopiaStyles,
  bodyXlRegularUtopiaStyles,
  headerMStyles,
  headerSStyles,
  headerXsStyles,
} from '../../styles/typography';
import { bpWidth, bpWidthTailwind, colors } from '../../styles/variables';
import { getStoryblokAssetDimensions, getStoryblokImageUrl } from '../../utils/storyblokAsset';
import { renderRichTextReact } from '../../utils/storyblokRichText';
import { CarouselFPTextBottomRow, CarouselFPTextRow, CarouselFPTextTopRow } from '../Carousels/CarouselStyles';
// The project's own Col, not react-styled-flexboxgrid's. This one resolves its widths to
// Tailwind responsive classes from its props alone, so the server-rendered HTML already has the
// correct layout. react-styled-flexboxgrid instead divides by a single theme.flexboxgrid.gridSize
// that Layout.jsx can only set correctly after hydration, which is what made every column on the
// page mis-sized on the first paint. `px-0` on each usage keeps the zero gutters these columns
// have always had — the Tailwind plugin defaults flexboxgrid.gutterWidth to 1 (0.5rem a side),
// whereas the styled-components themes all set it to 0.
import Col from '../FlexboxGrid/Col';

const SectionCol = styled.div`
  width: 100%;
  height: 100vh;
  position: relative;
  z-index: 10;

  @supports (height: 100dvh) {
    & {
      ${({ starWarsTheme }) => (starWarsTheme ? 'height: calc(100dvh - 60px);' : 'height: 100dvh;')};
    }
  }

  .hero-control-play {
    svg {
      g#pause-play-button-L {
        g#outline-2 {
          stroke: #f2f1ee;
        }

        g#pause {
          rect#Rectangle_963-2,
          rect#Rectangle_964-2 {
            fill: #f2f1ee;
          }
        }

        path#play-2 {
          fill: #f2f1ee;
        }
      }
    }

    &:hover {
      svg {
        g#pause-play-button-L {
          g#outline-2 {
            stroke: #f2f1ee;
          }

          g#pause {
            rect#Rectangle_963-2,
            rect#Rectangle_964-2 {
              fill: #f2f1ee;
            }
          }

          path#play-2 {
            fill: #f2f1ee;
          }
        }
      }
    }

    @media (${bpWidthTailwind.desktopSm}) {
      svg {
        g#pause-play-button-L {
          g#outline-2 {
            stroke: rgba(242, 241, 238, 0.5);
          }

          g#pause {
            rect#Rectangle_963-2,
            rect#Rectangle_964-2 {
              fill: rgba(242, 241, 238, 0.5);
            }
          }

          path#play-2 {
            fill: rgba(242, 241, 238, 0.5);
          }
        }
      }
    }
  }
`;

const MediaRow = styled.div`
  width: 100%;
  height: 100%;
`;

const Title = styled.div`
  ${headerMStyles};
  color: ${colors.beige100};

  @media (${bpWidth.desktopSm}) {
    ${headerSStyles};
    color: ${colors.beige100};
  }

  @media (${bpWidth.mobile}) {
    ${headerXsStyles};
    color: ${colors.beige100};
  }

  img {
    display: inline-block;
  }

  img:nth-of-type(1) {
    margin-bottom: -9px;
    height: auto;
    width: 110px;

    @media (${bpWidth.desktopSm}) {
      width: 98px;
      margin-bottom: -8px;
    }

    @media (${bpWidth.mobile}) {
      width: 72px;
      margin-bottom: -5px;
    }
  }

  img:nth-of-type(2) {
    fill: #ffffff;
    margin-bottom: -8px;
    height: auto;
    width: 110px;

    * {
      fill: #ffffff;
    }

    @media (${bpWidth.desktopSm}) {
      width: 98px;
      margin-bottom: -8px;
    }

    @media (${bpWidth.mobile}) {
      width: 72px;
      margin-bottom: -5px;
    }
  }
`;

const Subtitle = styled.div`
  ${bodyXlRegularUtopiaStyles};
  color: ${colors.beige100};
  padding-right: 20px;

  @media (${bpWidth.desktopSm}) {
    ${bodyLRegularUtopiaStyles};
    color: ${colors.beige100};
  }

  p {
    display: inline;
  }

  /* styling for colored Father's Day code */
  h6 {
    display: inline;
    color: ${colors.cerulean100};

    ::before {
      content: ' ';
    }
  }
`;

const ShopButton = styled.button`
  ${btnSmPrimaryThinOrangeStyles};
  cursor: pointer;
  width: auto;
  min-width: 149px;
  padding: 0 20px;
  pointer-events: auto;

  transition: box-shadow 0.1s ease-in-out;

  :hover {
    border-color: ${colors.orange700};
    box-shadow: 0 0 20px 5px ${colors.orange400};
    background-color: ${colors.orange400};
  }

  @media (${bpWidthTailwind.desktopLg}) {
    ${btnLgPrimaryThinOrangeStyles};
    background-color: ${colors.orange400};
    cursor: pointer;
    width: auto;
    min-width: 220px;
    padding: 0 20px;

    :hover {
      border-color: ${colors.orange700};
      background-color: ${colors.orange400};
    }
  }
`;

const Hero = ({ blok }) => {
  const { title, starWarsTheme = false, subtitle } = blok;
  const loadType = 'eager';

  const { settings, dispatchSetting } = useAppState();
  const [isPlay, setIsPlay] = useState(false);
  const sectionRef = useRef(null);
  const videoRef = useRef(null);
  const tickingRef = useRef(false);

  useIsomorphicLayoutEffect(() => {
    const checkWindowPageYOffset = () => {
      if (typeof window !== 'undefined') {
        if (window.scrollY > sectionRef.current.offsetHeight) {
          dispatchSetting({
            name: inputLabels.navFontColorLight,
            value: false,
          });
        } else {
          dispatchSetting({
            name: inputLabels.navFontColorLight,
            value: settings.navLightMode,
          });
        }
      }
    };

    const onScroll = () => {
      if (tickingRef.current) return;
      tickingRef.current = true;
      requestAnimationFrame(() => {
        checkWindowPageYOffset();
        tickingRef.current = false;
      });
    };

    checkWindowPageYOffset();

    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', onScroll);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('scroll', onScroll);
      }
    };
  }, [settings.navLightMode]);

  const asset = blok.image?.[0];
  const isImage = asset?.mediaType === 'image';
  const isVideo = asset?.mediaType === 'video';
  const imageBlok = isImage ? asset : null;
  const videoBlok = isVideo ? asset : null;

  // Art-directed <picture> sources cover every breakpoint, so the browser's own
  // media-query matching picks the right one with no viewWidth/JS branching needed.
  const imageData = useMemo(() => {
    if (!isImage) return null;

    // eslint-disable-next-line max-len
    const imageUrl =
      asset?.assetLg?.filename || asset?.assetMd?.filename || asset?.assetSm?.filename || asset?.assetXs?.filename;

    if (!imageUrl) return null;

    const options = {
      layout: 'constrained',
      breakpoints: [414, 768, 1280, 1920],
      width: 1920,
    };
    const artDirected = [];
    if (asset?.assetXs?.filename) {
      artDirected.push({
        media: `(${bpWidth.mobile})`,
        image: getGatsbyImage(asset.assetXs.filename, { ...options, width: 414 }),
      });
    }
    if (asset?.assetSm?.filename) {
      artDirected.push({
        media: `(${bpWidth.tablet})`,
        image: getGatsbyImage(asset.assetSm.filename, { ...options, width: 768 }),
      });
    }
    if (asset?.assetMd?.filename) {
      artDirected.push({
        media: `(${bpWidth.desktopSm})`,
        image: getGatsbyImage(asset.assetMd.filename, { ...options, width: 1280 }),
      });
    }

    return withArtDirection(getGatsbyImage(imageUrl, options), artDirected);
  }, [asset, isImage]);

  // Breakpoint variants, smallest first, ending in an unconditional fallback.
  //
  // These deliberately are NOT rendered as <source media="..."> children. A <video> with a list
  // of sources re-runs the HTML resource selection algorithm every time a media query flips, and
  // each re-run downloads another complete file — the browser does not reuse the one it already
  // has. Measured on this hero (~1.7MB per variant): a static mobile load fetches 1 file, but
  // loading at desktop and resizing to mobile fetches 2, and sweeping 412 -> 768 -> 1024 -> 1440
  // fetches more again. PageSpeed Insights changes the viewport several times while setting up
  // emulation and capturing screenshots, so a real PSI run pulled all four variants: 7,033 KiB
  // from a.storyblok.com against the ~1,693 KiB actually needed.
  //
  // <picture>/<source media> is still right for images (see imageData above) — an <img> settles
  // on one candidate and re-selection is cheap. It is specifically wrong for multi-megabyte
  // video. Resolving to a single src below is the one place in this component where a JS-computed
  // choice beats the CSS-native one, and it is justified by that measurement rather than by taste.
  const videoSources = useMemo(() => {
    if (!isVideo) return [];

    const sources = [];
    if (asset?.assetXs?.filename) {
      sources.push({ media: `(${bpWidth.mobile})`, src: asset.assetXs.filename });
    }
    if (asset?.assetSm?.filename) {
      sources.push({ media: `(${bpWidth.tablet})`, src: asset.assetSm.filename });
    }
    if (asset?.assetMd?.filename) {
      sources.push({ media: `(${bpWidth.desktopSm})`, src: asset.assetMd.filename });
    }
    // eslint-disable-next-line max-len
    const fallbackSrc =
      asset?.assetLg?.filename || asset?.assetMd?.filename || asset?.assetSm?.filename || asset?.assetXs?.filename;
    if (fallbackSrc) {
      sources.push({ src: fallbackSrc });
    }

    return sources;
  }, [asset, isVideo]);

  // Resolve the variant once, imperatively, and never touch it again.
  //
  // The src is deliberately not a React-managed prop. useStoryblokSSG re-runs
  // JSON.parse(entry.content) on every render, so `blok` — and therefore `asset` and the memo
  // above — are new objects each time; any state or dependency array derived from them re-fires on
  // ordinary re-renders, and a re-render that follows a viewport change re-runs matchMedia and
  // swaps the src, downloading a second multi-megabyte file. Reading the DOM property directly is
  // immune to that: if the element already has a src, there is nothing to reconsider, no matter how
  // many times React re-renders or what the viewport did in the meantime.
  useEffect(() => {
    const el = videoRef.current;
    if (!el || el.getAttribute('src') || !videoSources.length) return;

    const match = videoSources.find((source) => !source.media || window.matchMedia(source.media).matches);
    if (match?.src) {
      el.setAttribute('src', match.src);
    }
  }, [videoSources]);

  // The poster is what actually paints as the hero (and is therefore the LCP element) —
  // the video only replaces it once enough of a ~1.7MB file has streamed in. It was being
  // served straight from Storyblok with no transform, i.e. at full quality: 140KiB for a
  // 414x800 image. Running it through the image service costs nothing visually and takes
  // the LCP paint down to a fraction of that.
  const posterFilename = useMemo(() => {
    if (!isVideo) return null;

    return (
      asset?.posterXs?.filename ||
      asset?.posterSm?.filename ||
      asset?.posterMd?.filename ||
      asset?.posterLg?.filename ||
      null
    );
  }, [asset, isVideo]);

  const posterData = useMemo(
    () => (posterFilename ? getStoryblokImageUrl(posterFilename, { quality: 70 }) : null),
    [posterFilename],
  );

  // Intrinsic dimensions for the <video> box, taken from the poster rather than the video:
  // video asset URLs are `/f/<space>/x/<hash>/<name>.webm` with no dimensions encoded, and
  // the poster is what determines the element's intrinsic size until metadata arrives
  // anyway. The container already pins the rendered height to 100dvh, so this is a
  // backstop rather than the main CLS lever.
  const videoDimensions = useMemo(() => getStoryblokAssetDimensions(posterFilename), [posterFilename]);

  const updateIsPlayingState = (value) => {
    setIsPlay(value);
  };

  const togglePlay = useCallback(() => {
    if (videoRef.current) {
      if (isPlay) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlay, videoRef.current]);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.addEventListener('play', updateIsPlayingState.bind(this, true));
      videoRef.current.addEventListener('pause', updateIsPlayingState.bind(this, false));
    }

    return () => {
      if (videoRef.current) {
        videoRef.current.removeEventListener('play', updateIsPlayingState.bind(this, true));
        // eslint-disable-next-line react-hooks/exhaustive-deps
        videoRef.current.removeEventListener('pause', updateIsPlayingState.bind(this, false));
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoRef.current]);

  const shopButtonClick = (e) => {
    e.preventDefault();

    navigate(PRODUCT_URL_GROGU);
  };

  return (
    // Deliberately not a <Col>. xs={8} md={10} lg={12} is "full width" at every breakpoint
    // (the grid is 8/10/12 columns respectively), but react-styled-flexboxgrid derives that
    // percentage from a single theme.flexboxgrid.gridSize that Layout.jsx can only correct in
    // an effect — so the server renders with gridSize=12 and 8/12 makes the hero 66.7% wide on
    // mobile until hydration snaps it to 100%. A plain full-width div is correct from the very
    // first paint with no JS involved. Gutters are 0 in every theme, so nothing else is lost.
    <div {...storyblokEditable(blok)} className={`w-full${starWarsTheme ? ' mt-[60px]' : ''}`}>
      <SectionCol ref={sectionRef} starWarsTheme={starWarsTheme}>
        <MediaRow>
          {imageData && (
            <GatsbyImage
              image={imageData}
              alt={imageBlok?.alt || imageBlok?.title || 'hero-image'}
              loading={loadType}
              fetchpriority="high"
              objectFit="cover"
              imgClassName={starWarsTheme ? 'object-center md:object-right' : 'object-bottom'}
              style={{
                width: '100vw',
                height: starWarsTheme ? 'calc(100vh - 60px)' : '100vh',
                maxWidth: '100%',
                position: 'absolute',
                top: '0',
                left: '0',
              }}
            />
          )}
          {videoBlok && (
            // `preload="none"` and the absence of fetchPriority are both about ordering,
            // not gating: autoplay still starts the video as soon as the browser can, but
            // the ~1.7MB stream no longer outranks the poster that actually paints the
            // hero. Previously `preload="auto"` + `fetchPriority="high"` put the video
            // ahead of its own poster, so the first hero frame waited on the video.
            <video
              key={videoBlok._uid}
              ref={videoRef}
              autoPlay
              loop
              muted
              playsInline
              preload="none"
              poster={posterData}
              width={videoDimensions?.width || undefined}
              height={videoDimensions?.height || undefined}
              className="w-full h-full object-cover"
            />
          )}
        </MediaRow>
        <CarouselFPTextRow>
          <Row>
            {/* Offsets become margin-left classes: this Col has no offset props, by design —
                xsOffset={1} is 1/8 of the mobile grid (12.5%) and mdOffset={0} resets it from
                the 10-column breakpoint up. */}
            <Col className="px-0 ml-[12.5%] md:ml-0" xs={6} sm={6} md={10} lg={12}>
              <CarouselFPTextTopRow>
                {/* mdOffset={1} => 1/10 = 10%; lgOffset={2} => 2/12 = 16.666666666666668% */}
                <Col className="px-0 md:ml-[10%] lg:ml-[16.666666666666668%]" xs={8} sm={6} md={5} lg={5}>
                  <Title>{renderRichTextReact(title)}</Title>
                </Col>
              </CarouselFPTextTopRow>
              <CarouselFPTextBottomRow className={`gap-y-7 sm:gap-y-5 ${starWarsTheme && '!pb-4'}`}>
                <Col className="px-0 md:ml-[10%] lg:ml-[16.666666666666668%]" xs={8} sm={6} md={5} lg={5}>
                  <div className="h-full flex items-end">
                    <Subtitle>{renderRichTextReact(subtitle)}</Subtitle>
                  </div>
                </Col>
                <Col className="px-0" xs={8} md={3} lg={4}>
                  <div className="w-full flex md:justify-end">
                    <a href={`${PRODUCT_URL_GROGU}`} target="_blank" rel="noreferrer" onClick={shopButtonClick}>
                      <ShopButton>Shop now</ShopButton>
                    </a>
                  </div>
                </Col>
              </CarouselFPTextBottomRow>
            </Col>
          </Row>
          {starWarsTheme && (
            <Row>
              <Col className="px-0" xs={8} md={10} lg={12}>
                <div className="text-white text-center pb-12 font-brown text-[10px] uppercase tracking-[1.8px]">
                  © & ™ Lucasfilm Ltd.
                </div>
              </Col>
            </Row>
          )}
        </CarouselFPTextRow>
        {videoRef.current && (
          <button
            type="button"
            className="absolute right-6 bottom-6 cursor-pointer hero-control-play"
            onClick={togglePlay}
          >
            {isPlay ? <PauseSvgIcon /> : <PlaySvgIcon />}
          </button>
        )}
      </SectionCol>
    </div>
  );
};

Hero.propTypes = {
  blok: PropTypes.object.isRequired,
};

export default Hero;
